import type {CropFrame, CropFrames} from '@PosterWhiteboard/poster/poster-media-crop/poster-media-crop.types';
import {degreesToRadians} from '@Utils/math.util';
import {CustomControlCursor} from '@Components/poster-editor/components/custom-item-controls/custom-item-controls.types';
import type {CropDragTarget, CropHandlePress, CropHandleSpec, ScenePoint, ScreenPoint} from '@Components/poster-editor/components/crop-handles-overlay/crop-handles-overlay.types';
import {
  CropDragKind,
  CROP_HANDLE_GRAB_REACH_PX,
  CROP_HANDLE_GRAB_REACH_WINDOW_FRACTION,
  CROP_HANDLE_MIDDLE_FRACTION,
  CROP_IMAGE_HANDLE_HIDE_DISTANCE_PX,
  CROP_ROTATION_SNAP_STEP_DEGREES,
  CROP_ROTATION_SNAP_TOLERANCE_DEGREES,
} from '@Components/poster-editor/components/crop-handles-overlay/crop-handles-overlay.types';

/** How far, in scene units, a press may sit from the edge or corner a window handle moves and still count as grabbing that handle. */
export const getHandleGrabReach = (frame: CropFrame, posterScale: number): number => {
  return Math.min(CROP_HANDLE_GRAB_REACH_PX / posterScale, Math.min(frame.width, frame.height) * CROP_HANDLE_GRAB_REACH_WINDOW_FRACTION);
};

/**
 * Whether a press that landed on a window handle sits within reach of every edge that handle moves. A
 * finger-sized handle covers much of a small window, so a press deeper inside is a move of the window.
 */
export const isPressWithinHandleReach = ({frame, spec, scenePoint, reach}: CropHandlePress): boolean => {
  const local = toFrameLocal(frame, scenePoint);
  const distanceToMovedEdgeX = getDistanceToMovedEdge(spec.fractionX, local.x + frame.width / 2, frame.width / 2 - local.x);
  const distanceToMovedEdgeY = getDistanceToMovedEdge(spec.fractionY, local.y + frame.height / 2, frame.height / 2 - local.y);
  return (distanceToMovedEdgeX === null || distanceToMovedEdgeX <= reach) && (distanceToMovedEdgeY === null || distanceToMovedEdgeY <= reach);
};

/** The press's distance to the edge a handle moves along one axis; `null` for a mid-edge handle, which moves neither end. */
const getDistanceToMovedEdge = (fraction: number, distanceToNearEdge: number, distanceToFarEdge: number): number | null => {
  if (fraction === CROP_HANDLE_MIDDLE_FRACTION) {
    return null;
  }
  return fraction === 0 ? distanceToNearEdge : distanceToFarEdge;
};

/** The point in the frame's own axes, origin at the frame's centre. */
const toFrameLocal = (frame: CropFrame, point: ScenePoint): ScenePoint => {
  const centre = getFrameCentre(frame);
  const angleRadians = degreesToRadians(frame.angle);
  const offsetX = point.x - centre.x;
  const offsetY = point.y - centre.y;
  return {
    x: offsetX * Math.cos(angleRadians) + offsetY * Math.sin(angleRadians),
    y: -offsetX * Math.sin(angleRadians) + offsetY * Math.cos(angleRadians),
  };
};

/**
 * Whether the crop window's corner sits close enough to the image's same corner that the two handles
 * would stack there. Both frames share one angle, so the same corner spec names the same corner on each.
 */
export const isImageCornerCrowdedByWindow = (frames: CropFrames, spec: CropHandleSpec, posterScale: number): boolean => {
  const imageCorner = getFrameCornerOnScreen(frames.image, spec, posterScale);
  const windowCorner = getFrameCornerOnScreen(frames.window, spec, posterScale);
  return Math.hypot(imageCorner.x - windowCorner.x, imageCorner.y - windowCorner.y) < CROP_IMAGE_HANDLE_HIDE_DISTANCE_PX;
};

/** The centre of a frame, in the same units as the frame. */
export const getFrameCentre = (frame: CropFrame): ScenePoint => {
  const angleRadians = degreesToRadians(frame.angle);
  return {
    x: frame.left + (frame.width / 2) * Math.cos(angleRadians) - (frame.height / 2) * Math.sin(angleRadians),
    y: frame.top + (frame.width / 2) * Math.sin(angleRadians) + (frame.height / 2) * Math.cos(angleRadians),
  };
};

/** The direction from a centre to a point, in degrees, clockwise positive like fabric's angle. */
export const getAngleAround = (centre: ScenePoint, point: ScenePoint): number => {
  return (Math.atan2(point.y - centre.y, point.x - centre.x) * 180) / Math.PI;
};

/** Brings a rotation into -180..180 and lets it click onto the nearest 45-degree step when it comes close. */
export const snapCropRotation = (rotation: number): number => {
  const signed = ((((rotation + 180) % 360) + 360) % 360) - 180;
  const nearestStep = Math.round(signed / CROP_ROTATION_SNAP_STEP_DEGREES) * CROP_ROTATION_SNAP_STEP_DEGREES;
  return Math.abs(signed - nearestStep) <= CROP_ROTATION_SNAP_TOLERANCE_DEGREES ? nearestStep : signed;
};

/** A frame corner on screen: the rotated top-left, plus the corner's offset spun by the frame's angle, in pixels. */
export const getFrameCornerOnScreen = (frame: CropFrame, spec: CropHandleSpec, posterScale: number): ScreenPoint => {
  const angleRadians = degreesToRadians(frame.angle);
  const offsetRight = spec.fractionX * frame.width;
  const offsetDown = spec.fractionY * frame.height;
  return {
    x: (frame.left + offsetRight * Math.cos(angleRadians) - offsetDown * Math.sin(angleRadians)) * posterScale,
    y: (frame.top + offsetRight * Math.sin(angleRadians) + offsetDown * Math.cos(angleRadians)) * posterScale,
  };
};

/** The body cursor held for a whole drag: the handle's own for a resize or image scale, and fixed cursors for a turn or a window move. */
export const getDragCursor = (dragTarget: CropDragTarget): CustomControlCursor => {
  if (dragTarget.kind === CropDragKind.ROTATE) {
    return CustomControlCursor.CURSOR_CROSSHAIR;
  }
  if (dragTarget.kind === CropDragKind.MOVE) {
    return CustomControlCursor.CURSOR_MOVE;
  }
  return dragTarget.spec.cursor;
};
